/**
 * usePublicMenuCart — cart state and upsell management for the public menu.
 * Separate from useCart.js which is used by the internal POS.
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { trackPixelEvent } from "@/hooks/useAnalytics";
import { trackAddToCart } from "@/utils/tracking";
import { bcTrack } from "@/utils/behavioralCommerce";
import { applySmartPricing } from "@/utils/smartPricingUtils";
import { useCheckoutTracking } from "@/hooks/useCheckoutTracking";

function _getFbCookies() {
  if (typeof document === "undefined") return {};
  const get = (name) => {
    const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    return m ? m[1] : undefined;
  };
  return { fbp: get("_fbp"), fbc: get("_fbc") };
}

function _newEventId() {
  return typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `atc_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

export function usePublicMenuCart({ smartPricing, restaurantId, API, restaurant, categories = [], authToken = "" }) {
  const [cart, setCart] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [checkoutSuggestions, setCheckoutSuggestions] = useState([]);
  const { trackEvent: trackCheckout } = useCheckoutTracking(restaurantId, authToken);

  // Build category id → name map for per-item eligibility checks
  const categoryMap = {};
  for (const cat of (categories || [])) {
    if (cat.id) categoryMap[cat.id] = cat.name || "";
  }

  // Derive totals with per-item eligibility — promotional products excluded from global discount
  const { subtotal: cartSubtotal, discountAmount: cartDiscountAmount, total: cartTotal } =
    applySmartPricing(cart, smartPricing, categoryMap);
  const cartCount = cart.reduce((sum, i) => sum + i.quantity, 0);

  const trackUpsell = (eventType, source, product = null) => {
    axios.post(`${API}/marketing/upsell-event`, {
      restaurant_id: restaurantId,
      event_type: eventType,
      source,
      product_id: product?.id || "",
      product_name: product?.name || "",
      product_price: product?.price || 0,
    }).catch(() => {});
  };

  const _fireAddToCartTracking = (productId, cartName, finalPrice, source = "menu") => {
    const eventId = _newEventId();
    const { fbp, fbc } = _getFbCookies();
    const currency = restaurant?.currency || "EUR";
    trackPixelEvent(
      "AddToCart",
      { value: finalPrice, currency, content_ids: [productId], content_name: cartName, content_type: "product" },
      { eventID: eventId },
    );
    trackAddToCart({
      product_id: productId,
      cart_value: finalPrice,
      fbp,
      fbc,
      metadata: { product_name: cartName, event_id: eventId, source },
    });
    bcTrack("add_to_cart", { product_id: productId, cart_value: finalPrice, metadata: { product_name: cartName } });
  };

  const addToCart = (product) => {
    const hasVariations = product.selectedVariations?.length > 0;
    const extraPrice = hasVariations
      ? product.selectedVariations.reduce((s, v) => s + (v.price || 0), 0)
      : 0;
    // Store original price; discount shown at order level, not item level
    const basePrice = product.price;
    const finalPrice = basePrice + extraPrice;
    const cartId = hasVariations
      ? `${product.id}_${product.selectedVariations.map(v => v.name).join("_")}`
      : product.id;
    const cartName = hasVariations
      ? `${product.name} (${product.selectedVariations.map(v => (v.quantity || 1) > 1 ? `${v.quantity}× ${v.name}` : v.name).join(", ")})`
      : product.name;

    setCart(prev => {
      const wasEmpty = prev.length === 0;
      const newCart = (() => {
        const existing = prev.find(i => i.cartId === cartId);
        if (existing) return prev.map(i => i.cartId === cartId ? { ...i, quantity: i.quantity + 1 } : i);
        return [...prev, { ...product, cartId, id: product.id, name: cartName, price: finalPrice, originalPrice: product.price, quantity: 1, selectedVariations: product.selectedVariations || [] }];
      })();
      if (wasEmpty) {
        trackCheckout("cart_started", {
          item_count: newCart.reduce((sum, i) => sum + i.quantity, 0),
          cart_value_minor: Math.round((newCart.reduce((sum, i) => sum + i.price * i.quantity, 0)) * 100),
          currency: restaurant?.currency || "EUR",
        });
      }
      return newCart;
    });
    _fireAddToCartTracking(product.id, cartName, finalPrice, "menu");
    toast.success(`${cartName} adicionado`);

    axios.get(`${API}/public/suggestions/${restaurantId}?product_id=${product.id}`)
      .then(res => {
        // Backend returns { suggestions: [...] } or directly [...]
        const raw = Array.isArray(res.data) ? res.data : (res.data?.suggestions || []);
        const sugs = raw.filter(s => s.id !== product.id);
        if (sugs.length > 0) {
          setSuggestions(sugs);
          setShowSuggestions(true);
          sugs.forEach(s => trackUpsell("shown", "popup", s));
        }
      }).catch(() => {});
  };

  const addToCartSilent = (product) => {
    const finalPrice = product.price;
    setCart(prev => {
      const existing = prev.find(i => i.cartId === product.id);
      if (existing) return prev.map(i => i.cartId === product.id ? { ...i, quantity: i.quantity + 1 } : i);
      return [...prev, { ...product, cartId: product.id, id: product.id, name: product.name, price: finalPrice, originalPrice: product.price, quantity: 1, selectedVariations: [] }];
    });
    _fireAddToCartTracking(product.id, product.name, finalPrice, "upsell");
    toast.success(`${product.name} adicionado`);
  };

  const addSuggestionToCart = (product) => {
    const finalPrice = product.price;
    setCart(prev => {
      const existing = prev.find(i => i.id === product.id && !i.selectedVariations?.length);
      if (existing) return prev.map(i => i.id === product.id ? { ...i, quantity: i.quantity + 1 } : i);
      return [...prev, { ...product, cartId: product.id, price: finalPrice, originalPrice: product.price, quantity: 1 }];
    });
    trackUpsell("accepted", "popup", product);
    _fireAddToCartTracking(product.id, product.name, finalPrice, "suggestion");
    toast.success(`${product.name} adicionado`);
    setShowSuggestions(false);
  };

  const updateQuantity = (cartId, delta) => {
    if (delta < 0) {
      const item = cart.find(i => i.cartId === cartId);
      if (item) {
        bcTrack("remove_from_cart", {
          product_id: item.id,
          cart_value: item.price * Math.min(item.quantity, Math.abs(delta)),
        });
      }
    }
    setCart(prev =>
      prev.map(i => i.cartId === cartId ? { ...i, quantity: Math.max(0, i.quantity + delta) } : i)
         .filter(i => i.quantity > 0)
    );
  };

  return {
    cart, setCart,
    cartTotal, cartSubtotal, cartDiscountAmount, cartCount,
    addToCart, addToCartSilent, addSuggestionToCart, updateQuantity,
    suggestions, setSuggestions, showSuggestions, setShowSuggestions,
    checkoutSuggestions, setCheckoutSuggestions,
    trackUpsell,
    _fireAddToCartTracking,
  };
}
